/*
 * Copyright (c) 2024. Tooling Team
 */

export const resolveBulkUploadFile = (bulkFile) => {
  if (!bulkFile) {
    return null;
  }

  if (bulkFile instanceof File) {
    return bulkFile;
  }

  if (bulkFile.file instanceof File) {
    return bulkFile.file;
  }

  if (bulkFile.originFileObj instanceof File) {
    return bulkFile.originFileObj;
  }

  return null;
};

//take in a json response and return the validated row structure
export const handleValidatedRows = (jsonResponse) => {
  let records;
  if (
    jsonResponse &&
    typeof jsonResponse === "object" &&
    !Array.isArray(jsonResponse) &&
    Object.hasOwn(jsonResponse, "records")
  ) {
    records = jsonResponse.records;
  } else {
    records = jsonResponse;
  }

  if (
    jsonResponse &&
    typeof jsonResponse === "object" &&
    typeof jsonResponse.error === "string" &&
    (!Array.isArray(records) || records.length === 0)
  ) {
    throw new Error(jsonResponse.error);
  }

  if (!Array.isArray(records)) {
    throw new Error("Bulk upload response did not include rows.");
  }

  let failed = {};
  let succeeded = [];

  for (const record of records) {
    if (Object.hasOwn(record, "errors") && record.errors.length > 0) {
      //assuming for now that each record can only fail validation for 1 reason.
      if (Object.hasOwn(failed, record.errors[0])) {
        failed[record.errors[0]].push(record.data);
      } else {
        failed[record.errors[0]] = [record.data];
      }
    } else {
      succeeded.push(record.data);
    }
  }
  return { succeededRows: succeeded, failedRows: failed };
};

export function getFailedRecordCount(props) {
  let finalCount = 0;
  if (props.formData.failedRows) {
    let rowArrays = Object.values(props.formData.failedRows);
    rowArrays.forEach(function (array) {
      finalCount += array.length;
    });
  }

  return finalCount;
}

export function getValidationMsg(props, actionType) {
  let validationMsg = "Not all rows could be " + actionType + ": ";
  if (!props.formData.failedRows) {
    validationMsg += "The file contains no rows.";
  } else if (Object.keys(props.formData.failedRows).length > 0) {
    if (Object.keys(props.formData.failedRows)[0] === "map not found") {
      validationMsg +=
        "Rows must be defined to upload to currently loaded map.";
    } else {
      validationMsg += Object.keys(props.formData.failedRows)[0];
    }
  } else {
    validationMsg = "All rows were successfully " + actionType + ".";
  }
  return validationMsg;
}

export function isDownloadDisabled(props) {
  return (
    !props.formData.failedRows ||
    Object.keys(props.formData.failedRows).length === 0
  );
}

export function getFormattedRows(props) {
  let rowData = [];
  let dataKeys = Object.keys(props.formData.failedRows);
  if (dataKeys.length > 0) {
    dataKeys.forEach(function (key) {
      //for each error
      props.formData.failedRows[key].forEach(function (obj) {
        rowData.push(extractRowObject(obj));
      });
    });
  }
  return rowData;
}

export function extractRowObject(obj) {
  //for each row object in the array
  let finalRowObject = JSON.parse(JSON.stringify(obj));
  let dataCols = Object.keys(obj);
  dataCols.forEach(function (col) {
    //for each key in that row object
    //if that value is itself another object
    if (obj[col] && typeof obj[col] === "object") {
      let innerCols = Object.keys(obj[col]);
      //get that object's inner column headers
      innerCols.forEach(function (innerCol) {
        finalRowObject[innerCol] = obj[col][innerCol];
      });
      //remove the object from the row now that we've replaced with the inner values
      delete finalRowObject[col];
    }
  });
  return finalRowObject;
}

export const downloadInvalidRows = (props, finalRowData) => {
  if (finalRowData.length > 0) {
    let csvString = rowsToCSVConverter(finalRowData);
    let trimmedFilename = props.formData.fileName.substring(
      0,
      props.formData.fileName.length - 4
    );
    //create mock element to allow for download of specifically-named file
    let encodedUri = encodeURI(csvString);
    let link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", trimmedFilename + "_failed.csv");
    document.body.appendChild(link);

    link.click();
  }
};

export function rowsToCSVConverter(finalRowData) {
  let csvRows = "data:text/csv;charset=utf-8,";
  let headers = Object.keys(finalRowData[0])
    .join("|")
    .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    .toUpperCase();
  csvRows += headers; // snake case
  finalRowData.forEach(function (row) {
    const values = Object.values(row);
    csvRows += "\n" + values.join("|");
  });
  return csvRows;
}

export function getBulkUploadFormData(validatedReturn, bulkFile) {
  const bulkUploadPreviewFormData = {};
  bulkUploadPreviewFormData.fileName = bulkFile.name;
  bulkUploadPreviewFormData.file = bulkFile;
  bulkUploadPreviewFormData.failedRows = validatedReturn.failedRows;
  bulkUploadPreviewFormData.succeededRows = validatedReturn.succeededRows;
  bulkUploadPreviewFormData.mode = "Bulk Upload";
  return bulkUploadPreviewFormData;
}
